/*
    Item content editor page
*/

import React, { useCallback, useEffect, useRef, useState } from 'react';
import Dashboard from '../dashboard';
import styles from '../../style/info-card.module.css';
import axios from 'axios';
import { EditOutlined, ReloadOutlined } from '@ant-design/icons';
import ItemContent from '../item-content/item-content';
import ItemContentContext from '../item-content/item-content-context';
import ItemContentToolbar from '../item-content/toolbar/item-content-toolbar';
import { PollingContext } from '../item-content/polling-context';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

const ContentEditorPageContent = ({ contentType }) => {
  const { itemID } = useParams();
  const token = useSelector((state) => state.token);

  const itemContentContext = useRef(null);

  const [contentID, setContentID] = useState('');
  const [document, setDocument] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  const fetchItem = useCallback(
    async (signal) => {
      const requestData = { itemID };
      const config = {
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Token ' + token
        },
        signal
      };

      const response = await axios.post(
        process.env.REACT_APP_API_URL + '/item/get',
        requestData,
        config
      );
      if (response.data == null) throw new Error('no response data');
      return response.data.item.content[contentType];
    },
    [itemID, token, contentType]
  );

  const fetchDocument = useCallback(
    async (id, signal) => {
      if (!id) return null;

      const requestData = { contentID: id };
      const config = {
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Token ' + token
        },
        signal
      };

      const response = await axios.post(
        process.env.REACT_APP_API_URL + '/content/getDocument',
        requestData,
        config
      );
      if (response.data == null) throw new Error('no response data');
      return response.data.document;
    },
    [token]
  );

  // Fetch item and document whenever itemID changes
  useEffect(() => {
    const abortController = new AbortController();

    const load = async () => {
      try {
        const newContentID = await fetchItem(abortController.signal);
        setContentID(newContentID);

        const newDocument = await fetchDocument(newContentID, abortController.signal);
        // Setting document to null then immediately to newDocument triggers the
        // useLayoutEffect in ItemContentContext so it picks up the new value.
        // This relies on React 17 NOT batching updates inside async functions.
        // If upgrading to React 18+, automatic batching may coalesce these into
        // a single update — use flushSync or a different approach if that happens.
        setDocument(null);
        setDocument(newDocument);
      } catch (error) {
        if (axios.isCancel(error) || error.name === 'AbortError') return;
        console.log(error);
        setContentID('');
        setDocument(null);
      }
    };

    setContentID('');
    setDocument(null);
    setIsEditing(false);
    load();

    return () => {
      abortController.abort();
    };
  }, [itemID, fetchItem, fetchDocument]);

  const reload = useCallback(async () => {
    try {
      const newContentID = await fetchItem();
      setContentID(newContentID);
      const newDocument = await fetchDocument(newContentID);
      setDocument(null);
      setDocument(newDocument);
    } catch (error) {
      console.log(error);
    }
  }, [fetchItem, fetchDocument]);

  const saveDocument = useCallback(async () => {
    const requestData = {
      contentID,
      document: itemContentContext.current.getValue(),
      s3FileNames: itemContentContext.current.getFileNames()
    };

    const config = {
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Token ' + token
      }
    };

    try {
      await axios.post(process.env.REACT_APP_API_URL + '/content/setDocument', requestData, config);
      setIsEditing(false);
      const newDocument = await fetchDocument(contentID);
      setDocument(null);
      setDocument(newDocument);
    } catch (error) {
      console.log(error);
    }
  }, [contentID, token, fetchDocument]);

  const endEditing = useCallback(async () => {
    setIsEditing(false);
    try {
      const newDocument = await fetchDocument(contentID);
      setDocument(null);
      setDocument(newDocument);
    } catch (error) {
      console.log(error);
    }
  }, [contentID, fetchDocument]);

  return (
    <Dashboard>
      <ItemContentContext readOnly={!isEditing} ref={itemContentContext} value={document}>
        <div className={styles['card']}>
          <div className={styles['header']}>
            <ItemContentToolbar
              cancelEditing={endEditing}
              contentID={contentID}
              saveChanges={saveDocument}
            />
            {
              // reload button
              !isEditing ? (
                <div className={styles['button-container']} onClick={reload}>
                  <ReloadOutlined />
                  <label className={styles['header-button-label']}>Reload</label>
                </div>
              ) : null
            }
            {
              // edit button
              !isEditing ? (
                <div
                  className={styles['button-container']}
                  onClick={() => {
                    setIsEditing(true);
                    reload();
                  }}
                >
                  <EditOutlined />
                  <label className={styles['header-button-label']}>Edit</label>
                </div>
              ) : null
            }
          </div>
          <div className={styles['item-content-container']}>
            <ItemContent />
          </div>
        </div>
      </ItemContentContext>
    </Dashboard>
  );
};

const ContentEditorPage = ({ contentType }) => {
  return (
    <PollingContext offline>
      <ContentEditorPageContent contentType={contentType} />
    </PollingContext>
  );
};

export default ContentEditorPage;
